import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Flex,
  IconButton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import PageLayout from '../../../components/page-layout';
import PageCard from '../../../components/page-card';
import EmptyState from '../../../components/states/EmptyState';
import { PeopleIllustration } from '../../../components/illustrations';
import SkeletonTable from '../../../components/states/SkeletonTable';
import { MdCardGiftcard, MdGroup, MdNotificationImportant, MdPriceChange } from 'react-icons/md';
import { useParams } from 'react-router-dom';
import { translate } from '../../../i18n';
import { useNavigateToUserDetails } from '../../_shared/calendar/hooks/useNavigateToUserDetails';
import { tableHeaderTextProps, tableProps, tableRowProps } from '../../../theme/constants';
import { typography } from 'theme/tokens';
import { useGetCohort } from '../hooks/useGetCohort';
import { useGetCohortMembers } from '../hooks/useGetCohortMembers';
import { useRemoveUserFromCohort } from '../hooks/useRemoveUserFromCohort';
import BulkGrantCreditsModal from '../../wallet-admin/components/BulkGrantCreditsModal';
import AddUsersToCohortModal from './AddUsersToCohortModal';
import AssignCouponToCohortModal from './AssignCouponToCohortModal';
import SendCohortNotificationModal from './SendCohortNotificationModal';

export default function CohortDetailPage() {
  const { cohortId } = useParams<{ cohortId: string }>();
  const toast = useToast();
  const navigateToUserDetails = useNavigateToUserDetails();

  const { data: cohort, isLoading: cohortLoading, isError: cohortError } = useGetCohort(cohortId);
  const { data: membersData, isLoading: membersLoading, isError: membersError } =
    useGetCohortMembers(cohortId);
  const removeUser = useRemoveUserFromCohort(cohortId || '');

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const secondaryTextColor = useColorModeValue('gray.600', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const {
    isOpen: isAddUsersOpen,
    onOpen: onAddUsersOpen,
    onClose: onAddUsersClose,
  } = useDisclosure();

  const {
    isOpen: isNotifyOpen,
    onOpen: onNotifyOpen,
    onClose: onNotifyClose,
  } = useDisclosure();

  const {
    isOpen: isAssignCouponOpen,
    onOpen: onAssignCouponOpen,
    onClose: onAssignCouponClose,
  } = useDisclosure();

  const {
    isOpen: isGrantCreditsOpen,
    onOpen: onGrantCreditsOpen,
    onClose: onGrantCreditsClose,
  } = useDisclosure();

  const handleRemoveUser = async (userId: string, userName: string) => {
    if (
      window.confirm(
        `${translate('cohort.confirm_remove_user')} ${userName}?`,
      )
    ) {
      try {
        await removeUser.mutateAsync(userId);
        toast({
          title: translate('cohort.user_removed_successfully'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } catch (error) {
        toast({
          title: translate('cohort.remove_user_failed'),
          description: error instanceof Error ? error.message : undefined,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    }
  };

  if (cohortLoading) {
    return (
      <PageLayout>
        <SkeletonTable rows={5} columns={4} />
      </PageLayout>
    );
  }

  if (cohortError || !cohort) {
    return (
      <PageLayout>
        <EmptyState
          illustration={<PeopleIllustration />}
          title={translate('cohort.not_found')}
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      {/* Cohort Header */}
      <PageCard
        showBackButton
        title={cohort.name}
        rightHeader={
          <Badge colorScheme="brand" fontSize="lg" p={2}>
            {cohort.memberCount || 0} {translate('cohort.members')}
          </Badge>
        }
      >
        <Box px="25px" pb="22px">
          {cohort.description && (
            <Text color={secondaryTextColor} fontSize="md" mb={4}>
              {cohort.description}
            </Text>
          )}
          <Flex gap={3} flexWrap="wrap">
            <Button leftIcon={<AddIcon />} colorScheme="brand" onClick={onAddUsersOpen}>
              {translate('cohort.add_users')}
            </Button>
            <Button leftIcon={<MdPriceChange />} variant='outline' onClick={onAssignCouponOpen}>
              {translate('cohort.assign_coupons')}
            </Button>
            <Button leftIcon={<MdNotificationImportant />} variant='outline' onClick={onNotifyOpen}>
              {translate('cohort.send_notification')}
            </Button>
            <Button leftIcon={<MdCardGiftcard />} variant='outline' onClick={onGrantCreditsOpen}>
              {translate('wallet_admin.grant_credits')}
            </Button>
          </Flex>
        </Box>
      </PageCard>

      {/* Members List */}
      <Card flexDirection="column" w="100%" px="0px">
        <Flex px="25px" mb="8px" justifyContent="space-between" align="center">
          <Text
            color={textColor}
            {...typography.pageTitle}
          >
            {translate('cohort.members')}
          </Text>
        </Flex>

        {membersLoading ? (
          <SkeletonTable rows={5} columns={4} />
        ) : membersError ? (
          <EmptyState
            illustration={<PeopleIllustration />}
            title={translate('cohort.error_loading_members')}
          />
        ) : !membersData || membersData.members.length === 0 ? (
          <EmptyState
            variant="first-use"
            illustration={<PeopleIllustration />}
            title={translate('cohort.no_members')}
            actionLabel={translate('cohort.add_first_users')}
            onAction={onAddUsersOpen}
          />
        ) : (
          <Box>
            <Table {...tableProps}>
              <Thead>
                <Tr>
                  <Th borderColor={borderColor}>
                    <Text {...tableHeaderTextProps}>
                      {translate('cohort.user')}
                    </Text>
                  </Th>
                  <Th borderColor={borderColor}>
                    <Text {...tableHeaderTextProps}>
                      {translate('cohort.email')}
                    </Text>
                  </Th>
                  <Th borderColor={borderColor}>
                    <Text {...tableHeaderTextProps}>
                      {translate('cohort.added_at')}
                    </Text>
                  </Th>
                  <Th borderColor={borderColor}>
                    <Text {...tableHeaderTextProps}>
                      {translate('cohort.actions')}
                    </Text>
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {membersData.members.map((member) => (
                  <Tr
                    key={member.userId}
                    {...tableRowProps}
                    onClick={() => navigateToUserDetails?.(member.userId)}
                  >
                    <Td borderColor="transparent">
                      <Flex align="center" gap={3}>
                        <Avatar
                          size="sm"
                          name={`${member.firstName} ${member.lastName}`}
                          src={member.avatar}
                        />
                        <Text color={textColor} fontSize="sm" fontWeight="700">
                          {member.firstName} {member.lastName}
                        </Text>
                      </Flex>
                    </Td>
                    <Td borderColor="transparent">
                      <Text color={textColor} fontSize="sm">
                        {member.email}
                      </Text>
                    </Td>
                    <Td borderColor="transparent">
                      <Text color={textColor} fontSize="sm">
                        {new Date(member.addedAt).toLocaleDateString()}
                      </Text>
                    </Td>
                    <Td borderColor="transparent" onClick={(e) => e.stopPropagation()}>
                      <IconButton
                        aria-label={translate('cohort.remove_user')}
                        icon={<DeleteIcon />}
                        size="sm"
                        colorScheme="red"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveUser(
                            member.userId,
                            `${member.firstName} ${member.lastName}`,
                          );
                        }}
                      />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}
      </Card>

      <AddUsersToCohortModal
        cohortId={cohortId!}
        isOpen={isAddUsersOpen}
        onClose={onAddUsersClose}
      />
      <AssignCouponToCohortModal
        cohortId={cohortId!}
        cohortName={cohort.name}
        isOpen={isAssignCouponOpen}
        onClose={onAssignCouponClose}
      />
      <SendCohortNotificationModal
        cohortId={cohortId!}
        isOpen={isNotifyOpen}
        onClose={onNotifyClose}
      />
      <BulkGrantCreditsModal
        cohortId={cohortId!}
        isOpen={isGrantCreditsOpen}
        onClose={onGrantCreditsClose}
      />
    </PageLayout>
  );
}
